export default function WhatIsTraining() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
          Что такое обучение персонала
        </h2>
        <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
          Разбираемся, что действительно стоит за этим понятием — без штампов и общих фраз
        </p>

        <div className="space-y-12">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Не просто тренинги — система развития</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Обучение персонала</strong> — это системный процесс развития профессиональных знаний, навыков и компетенций сотрудников компании, направленный на достижение конкретных бизнес-целей. Это не разовое мероприятие, не «давайте соберём всех в зале и расскажем что-то полезное». Это выстроенная экосистема, которая работает непрерывно.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Ключевое отличие системы обучения от{' '}
              <a href="/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie" className="text-teal-600 font-semibold underline hover:text-teal-700">разовых тренингов</a> — в наличии <strong>цикла развития</strong>. Сотрудник не просто проходит тренинг и возвращается к работе. Он проходит диагностику, обучение, практику, получает обратную связь, закрепляет навыки и через время снова проходит оценку. Это замкнутый цикл, который обеспечивает реальное изменение поведения.
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Что входит в систему обучения персонала</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: 'Диагностика компетенций', desc: 'Аудит текущего уровня знаний и навыков. Интервью с руководителями. Анализ KPI и бизнес-показателей. Определение точек роста.' },
                { title: 'Матрица компетенций', desc: 'Систематизация требований к каждой должности. Какими навыками должен обладать сотрудник на каждом уровне. Что такое «хорошо» и «отлично» в его работе.' },
                { title: 'Карта обучения', desc: 'План развития для каждого сотрудника или группы. Какие программы пройти, в каком порядке, за какой период.' },
                { title: 'Программы и форматы', desc: 'Тренинги, воркшопы, онлайн-курсы, наставничество, бизнес-игры, стратегические сессии — выбор формата под задачу.' },
                { title: 'Практика и внедрение', desc: 'Домашние задания, проектная работа, отработка навыков в реальных условиях. 80% времени — практика.' },
                { title: 'Оценка результатов', desc: 'Замеры до и после обучения. KPI, конверсия, качество, NPS. Отчёт для руководителя с конкретными цифрами.' },
                { title: 'База знаний', desc: 'Накопление внутренних экспертных материалов. Скрипты, чек-листы, видеоуроки, кейсы. Знания остаются в компании.' },
                { title: 'Повторное развитие', desc: 'Цикл не заканчивается. Новые цели, новые компетенции, новый уровень. Развитие — непрерывный процесс.' }
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Чем система обучения отличается от разового тренинга</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-600 font-semibold">Параметр</th>
                    <th className="text-left py-3 px-4 text-red-600 font-semibold">Разовый тренинг</th>
                    <th className="text-left py-3 px-4 text-teal-600 font-semibold">Система обучения</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Длительность эффекта', '2-4 недели', 'Постоянно'],
                    ['Охват сотрудников', 'Одна группа', 'Вся компания'],
                    ['Адаптация под бизнес', 'Минимальная', 'Полная, после диагностики'],
                    ['Оценка результата', 'Анкета «понравилось/нет»', 'KPI, ROI, изменение поведения'],
                    ['Накопление знаний', 'Нет', 'База знаний, скрипты, кейсы'],
                    ['Повторение', 'Нет', 'Регулярное, по плану развития'],
                    ['Роль руководителя', 'Пассивная', 'Активная — участвует во внедрении'],
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-3 px-4 font-medium text-gray-900">{row[0]}</td>
                      <td className="py-3 px-4 text-red-600">{row[1]}</td>
                      <td className="py-3 px-4 text-teal-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Какие задачи решает обучение персонала</h3>
            <p className="text-gray-700 leading-relaxed">
              Система обучения персонала решает не одну, а целый комплекс бизнес-задач. Это не только «научить сотрудников работать лучше». Это инструмент, который влияет на прибыль, текучесть, масштабирование, качество сервиса, корпоративную культуру и конкурентоспособность компании. Правильно выстроенная система обучения — это стратегический актив бизнеса.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}